import { z } from 'zod/v4';

/** Above the ~953 ids in the seeded templates, so "select all" round-trips. */
export const DATA_POINT_EXPORT_MAX_IDS = 1000;

/**
 * Hard ceiling on the Companies sheet, enforced by the export job.
 *
 * Excel's own limit is 16,384 columns. Today's worst case is ~981 (31 company
 * columns + the catalogue), so this is nowhere near reachable — which is exactly
 * why it needs to be stated rather than left as an accident of
 * `DATA_POINT_EXPORT_MAX_IDS`. Raising that cap without this guard would produce
 * a workbook Excel refuses to open, and the failure would surface as a corrupt
 * download rather than an error anyone can act on.
 */
export const EXPORT_SHEET_MAX_COLUMNS = 16384;

const dataPointIdList = z
  .array(z.string().min(1))
  .max(DATA_POINT_EXPORT_MAX_IDS);

/**
 * Which sustainability data points to append as columns.
 *
 * `all` carries *exclusions* rather than inclusions so a data point added to the
 * catalogue later is picked up automatically; `only` carries inclusions so a
 * hand-picked set stays exactly that size. Neither shape is redundant — each is
 * the compact form of one half of the interaction space.
 *
 * `includeUnanswered` only exists on `all`: `only` emits every id it was given,
 * answered or not, so the flag would be meaningless there.
 */
export const ExportNetworkDataPointsSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('none') }),
  z.object({
    mode: z.literal('all'),
    except: dataPointIdList.optional().default([]),
    includeUnanswered: z.boolean().optional().default(false),
  }),
  /**
   * At least one id: an emptied picker is `mode: 'none'`, not an empty `only`.
   * Making the illegal state unrepresentable here beats a handler branch, since
   * the dialog's selection algebra already collapses to `none` on its own.
   */
  z.object({ mode: z.literal('only'), ids: dataPointIdList.min(1) }),
]);

export type ExportNetworkDataPoints = z.infer<
  typeof ExportNetworkDataPointsSchema
>;

/**
 * The column list, authoritative. Every preset sends one; only the company id
 * is mandatory, so a selection can be one column wide and mean it.
 */
export const ExportNetworkColumnsSchema = z
  .object({
    mode: z.literal('custom'),
    keys: z.array(z.string()),
  })
  .strip();

export type ExportNetworkColumns = z.infer<typeof ExportNetworkColumnsSchema>;

/** Standards and audit types are a far smaller space than the data points. */
export const EXPORT_ASSET_SELECTION_MAX_IDS = 500;

const assetIdList = z
  .array(z.string().min(1))
  .max(EXPORT_ASSET_SELECTION_MAX_IDS);

/**
 * Which certification standards or audit types to emit rows for.
 *
 * The same `all`-carries-exclusions / `only`-carries-inclusions shape as
 * `ExportNetworkDataPointsSchema`, minus `includeUnanswered`: a standard nobody
 * in the network holds contributes no rows at all, so there is nothing for the
 * flag to reveal.
 */
export const ExportNetworkAssetSelectionSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('none') }),
  z.object({
    mode: z.literal('all'),
    except: assetIdList.optional().default([]),
  }),
  z.object({ mode: z.literal('only'), ids: assetIdList.min(1) }),
]);

export type ExportNetworkAssetSelection = z.infer<
  typeof ExportNetworkAssetSelectionSchema
>;

/**
 * NOTE: `dataPoints` is `.optional()` with no top-level `.default()` on purpose.
 * `ExportNetworkRequest` is the *output* type and the old frontend types a
 * function parameter with it, so a defaulted key becomes required and every
 * existing caller stops compiling. The default is resolved in the route handler.
 */
export const ExportNetworkRequestSchema = z
  .object({
    ids: z.array(z.string().min(1)).max(500).optional().default([]),
    /** Absent = every column, where a caller sending `columnKeys` now lands. */
    columns: ExportNetworkColumnsSchema.optional(),
    /** Absent = today's behaviour, no data point columns. */
    dataPoints: ExportNetworkDataPointsSchema.optional(),
    /**
     * Absent or `none` = no sheet at all, which is what keeps every workbook the
     * current presets produce byte-identical. The sheets are opt-in, never
     * added unconditionally.
     */
    certifications: ExportNetworkAssetSelectionSchema.optional(),
    audits: ExportNetworkAssetSelectionSchema.optional(),
    documents: ExportNetworkAssetSelectionSchema.optional(),
    /**
     * Adds the acting company's own row. Its data points are self-declared
     * rather than the client's view of a supplier — same columns, different
     * provenance.
     */
    includeOwnCompany: z.boolean().optional(),
  })
  .strip();

export type ExportNetworkRequest = z.infer<typeof ExportNetworkRequestSchema>;
